import { cloneDeep } from "lodash";
import { observer } from "mobx-react";
import * as React from "react";
import { useLocation, useParams } from "react-router";
import { t } from "../../../i18n/util";
import { API } from "../../../network/API";
import { generalStore } from "../../../stores/GeneralStore";
import { EMPTY_APPLICANT, hrStore } from "../../../stores/HrStore";
import { withParams } from "../../app/router/history";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { ContentWrapper } from "../../ui/ContentWrapper";
import { TabConfig, TabBar } from "../../ui/TabBar";
import { ApplicantIdParam } from "../../util/helpers";
import { ApplicantDetailsHeader } from "../ApplicantDetailsHeader";
import { HrApplicantInformationRouter } from "../router/HrApplicantInformationRouter";
import { HrRoutes } from "../router/HrRoutes";

type ApplicantDetailsTabValues = "details" | "documents" | "notes" | "history";

const getTabs: (applicantId: string) => TabConfig<ApplicantDetailsTabValues>[] = (applicantId: string) => [
    {
        value: "details",
        label: t("screen.applicantDetails.details"),
        url: withParams(HrRoutes.APPLICANT.DETAILS, { applicantId })
    },
    {
        value: "documents",
        label: t("screen.applicantDetails.documents"),
        url: withParams(HrRoutes.APPLICANT.DOCUMENTS, { applicantId })
    },
    {
        value: "notes",
        label: t("screen.applicantDetails.notes"),
        url: withParams(HrRoutes.APPLICANT.NOTES, { applicantId })
    },
    {
        value: "history",
        label: t("screen.applicantDetails.history"),
        url: withParams(HrRoutes.APPLICANT.HISTORY, { applicantId })
    }
];

export const ApplicantInformationContainer = observer(() => {
    const { applicantId } = useParams<ApplicantIdParam>();
    const { pathname } = useLocation();
    const [value, setValue] = React.useState<ApplicantDetailsTabValues>("details");
    const isMobile = useMobileMediaQuery();

    React.useEffect(() => {
        return () => {
            hrStore.currentApplicantID = "";
            hrStore.currentApplicant = cloneDeep(EMPTY_APPLICANT);
        };
    }, []);

    React.useEffect(() => {
        hrStore.loadApplicant(applicantId);

        const readApplicant = async () => {
            await API.postReadApplicant(applicantId);
        };

        readApplicant();
    }, [applicantId]);

    React.useEffect(() => {
        const index = getTabs(applicantId).findIndex((tab) => pathname === tab.url);
        if (index >= 0) {
            setValue(getTabs(applicantId)[index].value);
        }
    }, [applicantId, pathname]);

    const handleChange = (event: React.ChangeEvent<any>, newValue: string) => {
        setValue(newValue as ApplicantDetailsTabValues);
    };

    return (
        <ContentWrapper style={{ padding: isMobile ? "0 16px" : "0 56px", maxWidth: 1400 }}>
            {!generalStore.hideNavigation && <ApplicantDetailsHeader />}
            {!generalStore.hideNavigation && !isMobile && (
                <TabBar value={value} handleChange={handleChange} tabs={getTabs(applicantId)} />
            )}
            {!generalStore.hideNavigation && isMobile && (
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <TabBar value={value} handleChange={handleChange} tabs={getTabs(applicantId)} />
                </div>
            )}
            <HrApplicantInformationRouter />
        </ContentWrapper>
    );
});
